import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import axios from "axios";
import { toast } from "react-toastify";
import HOST_URL from "../../utils/Url"; // Adjust the path as needed
import {
    Box,
    Drawer as MuiDrawer,
    AppBar as MuiAppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Menu,
    MenuItem,
    Collapse,
    SwipeableDrawer,
    Breadcrumbs,
    Link,
    Autocomplete,
    InputAdornment,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Card,
    CardContent,
    CardHeader,
    FormControl,
    InputLabel,
    Select,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    useMediaQuery,
} from "@mui/material";
import { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Home as HomeIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Folder as FolderIcon,
    SubdirectoryArrowRight,
    Search as SearchIcon,
    Close as CloseIcon,
    Brightness5,
    Brightness4,
    Waves,
    WbSunny,
    Forest,
    Flag,
    AccountCircle,
    ExitToApp,
    Settings,
    Palette,
    TextFields as FontIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

// Import specific assets from DDMSS
import logo from "../../assets/images/recyclebinLogo.png";
import loged from "../../assets/images/DrawerLogo.png";
import trans from "../../assets/images/translation.png";
import help from "../../assets/images/helpdesk.png";

// Import the theme CSS
import "./ThemeStyle.css";

// Import encryption utilities
import { secureGetItem, secureRemoveItem } from "../../utils/encryption";

const drawerWidth = 240;

// --- Styled Components ---
const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    borderRight: "none",
    boxShadow: theme.shadows[2],
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    borderRight: "none",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    minHeight: '70px !important',
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBarStyled = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    // Uses variables from ThemeStyle.css
    background: 'var(--header-background)',
    color: 'var(--header-color)',
    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.1), 0px 4px 5px 0px rgba(0,0,0,0.08), 0px 1px 10px 0px rgba(0,0,0,0.06)',
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const DrawerStyled = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "& .MuiDrawer-paper": {
        // Uses variables from ThemeStyle.css
        backgroundColor: 'var(--drawer-background)',
        color: 'var(--drawer-color)',
        borderRight: 'none',
        ...(open ? openedMixin(theme) : closedMixin(theme)),
    },
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
    }),
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
    minHeight: 48,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    justifyContent: "initial",
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    marginBottom: theme.spacing(0.5),
    borderRadius: '8px',
    margin: theme.spacing(0.5, 1.5),
    transition: 'background-color 0.3s ease, color 0.3s ease',
    '&:hover': {
        // Uses calculated RGB variable for transparency
        backgroundColor: 'rgba(var(--drawer-color-rgb, 0, 0, 0), 0.08)',
    },
    '&.Mui-selected': {
        // Uses variables from ThemeStyle.css
        backgroundColor: 'var(--header-background)',
        color: 'var(--header-color)',
        '& .MuiListItemIcon-root': {
            color: 'var(--header-color)',
        },
        '&:hover': {
            backgroundColor: 'var(--header-background)',
            opacity: 0.9,
        },
    },
}));

const StyledListItemIcon = styled(ListItemIcon)({
    minWidth: 0,
    justifyContent: "center",
    marginRight: '16px',
    color: 'inherit',
    transition: 'color 0.3s ease',
});

const StyledListItemText = styled(ListItemText)({
    opacity: 1,
    transition: 'opacity 0.3s ease',
    // Font size should be inherited from body via CSS now
    '& .MuiTypography-root': {
        fontWeight: 500,
    },
});

// --- Helper Functions ---
function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return { text: "Good Morning", icon: "🌅" };
    if (hour < 17) return { text: "Good Afternoon", icon: "🌞" };
    return { text: "Good Evening", icon: "🌜" };
}

function getInitials(name: string = ""): string {
    if (!name) return "U";
    return name
        .split(' ')
        .map(word => word?.charAt(0))
        .filter(char => char && /[a-zA-Z]/.test(char))
        .slice(0, 2)
        .join('')
        .toUpperCase();
}

// --- User Details Interface ---
interface UserDetails {
    fullName: string;
    userID: string;
    loginName: string;
    roleName: string;
    isActive: boolean;
    initials: string;
    email?: string;
    mobile?: string;
}

const defaultUserDetails: UserDetails = {
    fullName: "User",
    userID: "N/A",
    loginName: "N/A",
    roleName: "N/A",
    isActive: false,
    initials: "U"
};

// --- Theme Configuration ---
const themes = [
    { name: "light-theme", icon: <Brightness5 />, label: "Light" },
    { name: "dark-theme", icon: <Brightness4 />, label: "Dark" },
    { name: "ocean-theme", icon: <Waves />, label: "Ocean" },
    { name: "sunset-theme", icon: <WbSunny />, label: "Sunset" },
    { name: "forest-theme", icon: <Forest />, label: "Forest" },
    { name: "bhagwa-theme", icon: <Flag />, label: "Bhagwa" },
];

// --- Font Settings Configuration (EXPANDED) ---
// Ensure you load these fonts in public/index.html !
const availableFontFamilies = [
    'System Default',
    'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Nunito', 'Poppins', 'Inter',
    'Source Sans Pro', 'Raleway', 'Ubuntu', 'Noto Sans', 'Merriweather',
    'Playfair Display', 'Lora', 'PT Serif', 'Noto Serif', 'Georgia',
    'Arial', 'Helvetica', 'Verdana', 'Times New Roman',
];

const fontSizes = [
    { label: 'Tiny', value: '0.75rem' },     // 12px
    { label: 'X-Small', value: '0.8125rem' }, // 13px
    { label: 'Small', value: '0.875rem' },   // 14px
    { label: 'Medium', value: '1rem' },      // 16px (Default)
    { label: 'Large', value: '1.125rem' },   // 18px
    { label: 'X-Large', value: '1.25rem' },    // 20px
    { label: 'XX-Large', value: '1.375rem' }, // 22px
    { label: 'Huge', value: '1.5rem' },      // 24px
];

const defaultFontFamily = 'System Default';
const defaultFontSize = '1rem'; // Default to Medium


// --- Main Component ---
export default function MiniDrawer({ items = [] }: { items: any[] }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // --- State ---
    const [open, setOpen] = React.useState(!isMobile);
    const [profileDrawerOpen, setProfileDrawerOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [openSubMenus, setOpenSubMenus] = React.useState<Record<string, boolean>>({});
    const [searchValue, setSearchValue] = React.useState("");
    const [isThemeDialogOpen, setIsThemeDialogOpen] = React.useState(false);
    const [isFontDialogOpen, setIsFontDialogOpen] = React.useState(false);
    const [userDetails, setUserDetails] = React.useState<UserDetails>(defaultUserDetails);
    const [currentDate, setCurrentDate] = React.useState(new Date());
    const [selectedTheme, setSelectedTheme] = React.useState(() => {
        return localStorage.getItem("theme") || themes[0].name;
    });
    const [selectedFontFamily, setSelectedFontFamily] = React.useState(() => {
        return localStorage.getItem("fontFamily") || defaultFontFamily;
    });
    const [selectedFontSize, setSelectedFontSize] = React.useState(() => {
        return localStorage.getItem("fontSize") || defaultFontSize;
    });
    const [menuData, setMenuData] = React.useState<any>("");

    // --- Language ---
    const changeLanguage = (language: string) => {
        i18n.changeLanguage(language);
        localStorage.setItem("preferredLanguage", language);
    };
    const currentLanguage = localStorage.getItem("preferredLanguage") || "en";
    const newLanguage = currentLanguage === "hi" ? "English" : "हिंदी";

    // Load menu data from encrypted storage
    React.useEffect(() => {
        const loadMenuData = () => {
            try {
                // Use secureGetItem instead of localStorage.getItem
                const data = secureGetItem("userdata");
                if (data && data.length > 0) {
                    const userPermissionData = data[0]?.userPermission;
                    if (userPermissionData && userPermissionData.length > 0) {
                        const menudata = userPermissionData[0]?.parentMenu;
                        for (let index = 0; index < menudata.length; index++) {
                            const childMenudata = menudata[index]?.childMenu;
                            const pathrow = childMenudata.find(
                                (x: any) => x.path === location.pathname
                            );

                            if (pathrow) {
                                console.log("data", pathrow);
                                setMenuData(pathrow.menuId);
                                break;
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error loading menu data from encrypted storage:", error);
                
                // Fallback: try regular localStorage
                try {
                    const dataString = localStorage.getItem("userdata");
                    if (dataString) {
                        const data = JSON.parse(dataString);
                        if (data && data.length > 0) {
                            const userPermissionData = data[0]?.userPermission;
                            if (userPermissionData && userPermissionData.length > 0) {
                                const menudata = userPermissionData[0]?.parentMenu;
                                for (let index = 0; index < menudata.length; index++) {
                                    const childMenudata = menudata[index]?.childMenu;
                                    const pathrow = childMenudata.find(
                                        (x: any) => x.path === location.pathname
                                    );

                                    if (pathrow) {
                                        setMenuData(pathrow.menuId);
                                        break;
                                    }
                                }
                            }
                        }
                    }
                } catch (fallbackError) {
                    console.error("Fallback also failed:", fallbackError);
                }
            }
        };

        loadMenuData();
    }, [location.pathname]);

    // Handle responsive behavior for drawer
    React.useEffect(() => {
        setOpen(!isMobile);
    }, [isMobile]);

    // --- Effects ---

    // Apply Theme Class and calculate RGBA equivalents
    React.useEffect(() => {
        // Apply theme class to body
        document.body.className = selectedTheme;
        localStorage.setItem("theme", selectedTheme);

        const setRgbVariable = (cssVarName: string, targetVarName: string, fallbackRgb: string) => {
            setTimeout(() => {
                try {
                    const computedStyle = window.getComputedStyle(document.body);
                    const colorValue = computedStyle.getPropertyValue(cssVarName).trim();
                    let rgb = fallbackRgb;

                    if (!colorValue) {
                        console.warn(`CSS variable ${cssVarName} not found or empty for theme ${selectedTheme}. Using fallback ${fallbackRgb}.`);
                    } else if (colorValue.startsWith('#')) {
                        const bigint = parseInt(colorValue.substring(1), 16);
                        if (!isNaN(bigint)) {
                            rgb = `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
                        } else {
                            console.warn(`Invalid hex value for ${cssVarName}: ${colorValue}. Using fallback ${fallbackRgb}.`);
                        }
                    } else if (colorValue.startsWith('rgb')) {
                        const match = colorValue.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
                        if (match) {
                            rgb = `${match[1]}, ${match[2]}, ${match[3]}`;
                        } else {
                            console.warn(`Could not parse RGB value for ${cssVarName}: ${colorValue}. Using fallback ${fallbackRgb}.`);
                        }
                    } else {
                        console.warn(`Unrecognized color format for ${cssVarName}: ${colorValue}. Using fallback ${fallbackRgb}.`);
                    }
                    document.documentElement.style.setProperty(targetVarName, rgb);
                } catch (error) {
                    console.error(`Error processing color variable ${cssVarName}:`, error);
                    document.documentElement.style.setProperty(targetVarName, fallbackRgb);
                }
            }, 50);
        };

        setRgbVariable('--drawer-color', '--drawer-color-rgb', '0, 0, 0');
        setRgbVariable('--header-color', '--header-color-rgb', '255, 255, 255');
        setRgbVariable('--menu-color', '--menu-color-rgb', '0, 0, 0');

    }, [selectedTheme]);

    // Apply Font Settings Globally
    React.useEffect(() => {
        const rootStyle = document.documentElement.style;

        if (selectedFontFamily === 'System Default') {
            rootStyle.removeProperty('--base-font-family');
        } else {
            rootStyle.setProperty('--base-font-family', `'${selectedFontFamily}'`);
        }

        rootStyle.setProperty('--base-font-size', selectedFontSize);

        localStorage.setItem("fontFamily", selectedFontFamily);
        localStorage.setItem("fontSize", selectedFontSize);

    }, [selectedFontFamily, selectedFontSize]);

    // Date/Time ticker
    React.useEffect(() => {
        const timer = setInterval(() => setCurrentDate(new Date()), 1000 * 60);
        return () => clearInterval(timer);
    }, []);

    // User Details Load
 // User Details Load - UPDATED FOR ENCRYPTION
    React.useEffect(() => {
        const loadUserDetails = () => {
            try {
                // Use secureGetItem instead of localStorage.getItem
                const data = secureGetItem("userdata");
                
                if (data) {
                    const userDetail = data?.[0]?.userdetail?.[0];

                    if (userDetail) {
                        const firstName = userDetail.firsT_NAME || "";
                        const middleName = userDetail.middlE_NAME || "";
                        const surname = userDetail.suR_NAME || "";
                        const fullName = [firstName, middleName, surname].filter(Boolean).join(' ');
                        setUserDetails({
                            fullName: fullName || "User",
                            userID: userDetail.useR_ID || userDetail.userId || userDetail.id || "N/A",
                            loginName: userDetail.logiN_NAME || "N/A",
                            roleName: userDetail.rolename || "N/A",
                            isActive: userDetail.iS_ACTIVE === true || userDetail.iS_ACTIVE === 'Y',
                            initials: getInitials(firstName || fullName),
                            email: userDetail.email || undefined,
                            mobile: userDetail.cuR_MOBILE || undefined,
                        });
                    } else {
                        console.warn("User detail structure not found. Using defaults.");
                        setUserDetails(defaultUserDetails);
                    }
                } else {
                    console.log("No user data found. Using default user details.");
                    setUserDetails(defaultUserDetails);
                }
            } catch (error) {
                console.error("Failed to get user data from encrypted storage:", error);
                
                // Fallback: try regular localStorage
                try {
                    const storedDDMSSData = localStorage.getItem("userdata");
                    if (storedDDMSSData) {
                        const DDMSSData = JSON.parse(storedDDMSSData);
                        const userDetail = DDMSSData?.[0]?.userdetail?.[0];

                        if (userDetail) {
                            const firstName = userDetail.firsT_NAME || "";
                            const middleName = userDetail.middlE_NAME || "";
                            const surname = userDetail.suR_NAME || "";
                            const fullName = [firstName, middleName, surname].filter(Boolean).join(' ');
                            setUserDetails({
                                fullName: fullName || "User",
                                userID: userDetail.useR_ID || userDetail.userId || userDetail.id || "N/A",
                                loginName: userDetail.logiN_NAME || "N/A",
                                roleName: userDetail.rolename || "N/A",
                                isActive: userDetail.iS_ACTIVE === true || userDetail.iS_ACTIVE === 'Y',
                                initials: getInitials(firstName || fullName),
                                email: userDetail.email || undefined,
                                mobile: userDetail.cuR_MOBILE || undefined,
                            });
                        }
                    }
                } catch (fallbackError) {
                    console.error("Fallback also failed:", fallbackError);
                    setUserDetails(defaultUserDetails);
                }
            }
        };

        loadUserDetails();
    }, []);

    // --- Handlers ---
    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleProfileDrawerToggle = () => setProfileDrawerOpen(!profileDrawerOpen);
    const handleSubMenuToggle = (key: string) => {
        setOpenSubMenus(prev => ({ [key]: !prev[key] }));
        if (!open) setOpen(true);
    };
    const handleNavigation = (path: string | undefined) => {
        if (path && path !== "#" && path !== "" && path !== null) {
            navigate(path);
            handleMenuClose();
            if (isMobile) {
                handleDrawerClose(); // Close drawer on mobile after navigation
            }
        } else {
            console.warn("Navigation attempt with invalid path:", path);
        }
    };
    const handleMyProfileClick = () => {
        handleProfileDrawerToggle();
        handleMenuClose();
    };
 const handleLogout = async () => {
    try {
        // Check if we have a valid user ID
        if (!userDetails.userID || userDetails.userID === "N/A") {
            console.error("Cannot logout: Invalid user ID");
            toast.error("Cannot logout: Invalid user ID");
            
            // Still proceed with local cleanup
            localStorage.clear();
            sessionStorage.clear();
            secureRemoveItem("devID");   // IMPORTANT
           navigate("/");
            //window.location.reload();
            return;
        }

        // Make API call to logout endpoint using axios
        const response = await axios.post(
            `${HOST_URL.HOST_URL2}USER/USERLOGOUT?userId=${userDetails.userID}`
        );

        if (response.data.isSuccess) {
            // Show success message
            toast.success(response.data.mesg || "Logout successful");
            
            // Clear storage and redirect after showing the message
            setTimeout(() => {
                localStorage.clear();
                sessionStorage.clear();
              navigate("/");
               // window.location.reload();
            }, 1000); // Wait 1 second to show the toast message
        } else {
            // Show error message from API
            toast.error(response.data.mesg || "Logout failed");
            
            // Still proceed with local cleanup
            localStorage.clear();
            sessionStorage.clear();
           navigate("/");
           // window.location.reload();
        }
    } catch (error) {
        console.error("Error calling logout API:", error);
        toast.error("Logout failed due to network error");
        
        // Still proceed with local cleanup
        localStorage.clear();
        sessionStorage.clear();
        navigate("/");
       // window.location.reload();
    }
};
    const handleThemeDialogOpen = () => { setIsThemeDialogOpen(true); handleMenuClose(); };
    const handleThemeDialogClose = () => setIsThemeDialogOpen(false);
    const handleThemeChange = (themeName: string) => { setSelectedTheme(themeName); handleThemeDialogClose(); };
    const handleFontDialogOpen = () => { setIsFontDialogOpen(true); handleMenuClose(); };
    const handleFontDialogClose = () => setIsFontDialogOpen(false);
    const handleFontFamilyChange = (event: any) => {
        setSelectedFontFamily(event.target.value as string);
    };
    const handleFontSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedFontSize((event.target as HTMLInputElement).value);
    };
    const applyFontSettings = () => {
        handleFontDialogClose();
    };
    const handleAutocompleteChange = (event: any, value: string | null) => {
        if (!value) return;
        const foundItem = allMenuItems.find(item => item.label === value);
        if (foundItem?.path) {
            handleNavigation(foundItem.path);
            setSearchValue("");
        } else {
            console.log("No path found for selected search item:", value);
        }
    };

    // --- Data Preparation ---
    const greeting = getGreeting();
    const formattedDate = dayjs(currentDate).format("ddd, D MMM YYYY");
    const formattedTime = dayjs(currentDate).format("h:mm A");

    const allMenuItems = React.useMemo(() => {
        const menuList: { label: string; path: string }[] = [];
        const processItem = (item: any) => {
            if (item.name && item.path && item.path !== "#") {
                menuList.push({ label: item.name, path: item.path });
            }
            if (item.items && Array.isArray(item.items)) {
                item.items.forEach(processItem);
            }
        };
        items.forEach(processItem);
        return menuList;
    }, [items]);

    const filteredDrawerItems = React.useMemo(() => {
        if (!searchValue) return items;
        const lowerSearch = searchValue.toLowerCase();

        const filterItems = (itemList: any[]): any[] => {
            return itemList.reduce((acc: any[], item) => {
                const itemMatches = item.name?.toLowerCase().includes(lowerSearch);
                let filteredChildren: any[] | null = null;
                if (item.items && Array.isArray(item.items)) {
                    filteredChildren = filterItems(item.items);
                }
                if (itemMatches || (filteredChildren && filteredChildren.length > 0)) {
                    acc.push({ ...item, items: filteredChildren ?? item.items });
                }
                return acc;
            }, []);
        };
        return filterItems(items);
    }, [items, searchValue]);

    // Generate Breadcrumbs
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbLinks = pathnames.map((value, index) => {
        const to = `/${pathnames.slice(0, index + 1).join('/')}`;
        const displayValue = value.charAt(0).toUpperCase() + value.slice(1).replace(/[-_]/g, ' ');
        return { name: displayValue, path: to };
    });

    // --- Render Logic ---
    const isMenuActive = (path: string | undefined) => !!path && location.pathname === path;
    const isSubMenuActive = (path: string | undefined) => !!path && location.pathname.startsWith(path);

    // Recursive Menu Item Renderer
    const renderMenuItems = (menuItems: any[], level = 0) => {
        return menuItems.map((item: any, index: number) => {
            const itemKey = item.id || item.path || `${item.name}-${level}-${index}`;
            const hasSubItems = item.items && Array.isArray(item.items) && item.items.length > 0;
            const isSubMenuOpen = !!openSubMenus[itemKey];
            const isActive = isMenuActive(item.path) || (hasSubItems && item.items.some((sub: any) => isSubMenuActive(sub.path)));

            if (hasSubItems) {
                return (
                    <React.Fragment key={itemKey}>
                        <StyledListItemButton
                            onClick={() => handleSubMenuToggle(itemKey)}
                            selected={isSubMenuOpen || (!Object.keys(openSubMenus).length && isActive && !isSubMenuOpen)}
                            sx={{ ...(open ? {} : { px: 2.5, justifyContent: 'center' }) }}
                            title={open ? "" : item.name}
                        >
                            <StyledListItemIcon sx={{ ...(open ? {} : { mr: 'auto' }) }}>
                                <FolderIcon />
                            </StyledListItemIcon>
                            <StyledListItemText primary={item.name} sx={{ opacity: open ? 1 : 0 }} />
                            {open && (isSubMenuOpen ? <ExpandLessIcon sx={{ ml: 1, opacity: 0.7 }} /> : <ExpandMoreIcon sx={{ ml: 1, opacity: 0.7 }} />)}
                        </StyledListItemButton>
                        <Collapse in={open && isSubMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{ pl: open ? 2 : 0 }}>
                                {renderMenuItems(item.items, level + 1)}
                            </List>
                        </Collapse>
                    </React.Fragment>
                );
            } else {
                return (
                    <StyledListItemButton
                        key={itemKey}
                        selected={isMenuActive(item.path)}
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                            ...(open ? { pl: 2.5 + level * 2 } : { px: 2.5, justifyContent: 'center' }),
                        }}
                        title={open ? "" : item.name}
                    >
                        <StyledListItemIcon sx={{
                            minWidth: '20px',
                            mr: open ? 1.5 : 'auto',
                            opacity: level > 0 ? 0.8 : 1,
                            ...(open ? {} : { mr: 'auto' })
                        }}>
                            {level > 0 ? <SubdirectoryArrowRight sx={{ fontSize: '1.1rem' }} /> : <FolderIcon />}
                        </StyledListItemIcon>
                        <StyledListItemText
                            primary={item.name}
                            sx={{
                                opacity: open ? 1 : 0,
                                '& .MuiTypography-root': {
                                    fontSize: level > 0 ? '0.9em' : '1em',
                                    fontWeight: level > 0 ? 400 : 500
                                }
                            }}
                        />
                    </StyledListItemButton>
                );
            }
        });
    };

    return (
        <Box sx={{
            display: "flex",
            height: '100vh',
            backgroundColor: 'var(--main-background)',
            backgroundImage: 'var(--background-image)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            overflow: 'hidden',
        }}>
            {/* === AppBar === */}
            <AppBarStyled position="fixed" open={open}>
                {/* Main Toolbar */}
                <Toolbar sx={{ minHeight: '64px !important' }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{ mr: 2, ...(open && { display: "none" }) }}
                    >
                        <MenuIcon />
                    </IconButton>
                    {!open && (
                        <img src={logo} width={40} height={40} alt="Logo Small" style={{ marginLeft: '5px', marginRight: '10px' }} />
                    )}
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 'bold', display: { xs: 'block', sm: 'block' }, textWrap: { xs: "wrap" } }}>
                        Digital Document Management System Software
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ display: { xs: 'none', md: 'block' }, mr: 1.5 }}>
                            {userDetails.fullName}
                        </Typography>
                        <IconButton
                            onClick={handleMenuOpen}
                            size="small"
                            sx={{ ml: 1 }}
                            aria-controls={anchorEl ? "account-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={anchorEl ? "true" : undefined}
                        >
                            <Avatar sx={{ width: 40, height: 40, bgcolor: 'var(--header-background1)', color: 'var(--header-color)', fontWeight: 'bold' }}>
                                {userDetails.initials}
                            </Avatar>
                        </IconButton>
                    </Box>
                </Toolbar>

                {/* Secondary Bar (Breadcrumbs, Time, etc.) */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                        px: { xs: 1.5, sm: 2.5 },
                        background: 'var(--header-background1)',
                        color: 'var(--header-color)',
                        minHeight: '36px',
                        borderBottomLeftRadius: open ? 0 : '12px',
                        borderBottomRightRadius: '12px',
                        transition: 'border-radius 0.3s ease',
                        boxShadow: 'inset 0px 1px 3px rgba(0,0,0,0.1)',
                    }}
                >
                    <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{
                        '& .MuiBreadcrumbs-separator': { mx: 0.5, color: 'rgba(var(--header-color-rgb, 255, 255, 255), 0.7)' },
                        '& a, & p': { color: 'var(--header-color)', textDecoration: 'none', display: 'flex', alignItems: 'center' },
                        '& a:hover': { opacity: 0.8 },
                        '& a': { cursor: 'pointer' }
                    }}>
                        <Link onClick={() => handleNavigation('/home')} sx={{ display: 'flex', alignItems: 'center' }}>
                            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Home
                        </Link>
                        {breadcrumbLinks.map((link, index) => {
                            const isLast = index === breadcrumbLinks.length - 1;
                            return isLast ? (
                                <Typography key={link.path} sx={{ color: 'inherit', fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                                    {link.name}
                                </Typography>
                            ) : (
                                <Link key={link.path} onClick={() => handleNavigation(link.path)} sx={{ display: 'flex', alignItems: 'center' }}>
                                    {link.name}
                                </Link>
                            );
                        })}
                    </Breadcrumbs>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>
                        <Typography variant="caption" sx={{ display: { xs: 'none', md: 'inline' }, color: 'inherit', fontWeight: 'bold' }}>
                            {greeting.icon} {greeting.text}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'inherit' }}>{formattedTime}</Typography>
                        <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'inline' }, color: 'inherit' }}>{formattedDate}</Typography>
                    </Box>
                </Box>

                {/* User Profile Menu Dropdown */}
                <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: "visible",
                            mt: 1.5,
                            maxHeight: '300px',
                            overflowY: 'auto',
                            bgcolor: 'var(--menu-background)',
                            color: 'var(--menu-color)',
                            borderRadius: '8px',
                            minWidth: '220px',
                            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.2))",
                            '& .MuiMenuItem-root': {
                                padding: '8px 16px',
                                '& .MuiListItemIcon-root': { minWidth: '36px', color: 'inherit' },
                                '&:hover': { backgroundColor: 'rgba(var(--menu-color-rgb, 0, 0, 0), 0.06)' }
                            },
                            "&::before": {
                                content: '""',
                                display: "block",
                                position: "absolute",
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: "var(--menu-background)",
                                transform: "translateY(-50%) rotate(45deg)",
                                zIndex: 0
                            },
                        },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                    <MenuItem onClick={handleMyProfileClick}>
                        <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon> Profile
                    </MenuItem>
                    <MenuItem onClick={handleThemeDialogOpen}>
                        <ListItemIcon><Palette fontSize="small" /></ListItemIcon> Change Theme
                    </MenuItem>
                    <MenuItem onClick={handleFontDialogOpen}>
                        <ListItemIcon><FontIcon fontSize="small" /></ListItemIcon> Font Settings
                    </MenuItem>
                    <MenuItem onClick={(e) => { e.stopPropagation(); changeLanguage(currentLanguage === "hi" ? "en" : "hi"); handleMenuClose(); }}>
                        <ListItemIcon>
                            <img src={trans} width={24} height={24} alt="Translate" style={{ opacity: 0.8 }} />
                        </ListItemIcon>
                        Translate ({newLanguage})
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            let path = "/Help/HelpDesk";
                            localStorage.setItem("menuData", menuData.toString());
                            window.open(path, "_blank");
                        }}
                    >
                        <ListItemIcon>
                            <img src={help} width={30} height={30} alt="Help Desk" />
                        </ListItemIcon>
                        Help Desk
                    </MenuItem>
                    <Divider sx={{ my: 0.5, bgcolor: 'var(--divider-background)' }} />
                    <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                        <ListItemIcon><ExitToApp fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon> Logout
                    </MenuItem>
                </Menu>
            </AppBarStyled>

            {/* === Drawer === */}
            <DrawerStyled variant="permanent" open={open}>
                <DrawerHeader>
                    {open && (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                            <img src={loged} alt="Logo Large" style={{ maxHeight: '50px', objectFit: 'contain' }} />
                        </Box>
                    )}
                    <IconButton onClick={handleDrawerClose} sx={{ color: 'var(--drawer-color)', '&:hover': { opacity: 0.8 } }}>
                        {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </DrawerHeader>
                <Divider sx={{ bgcolor: 'var(--divider-background)', opacity: 0.5 }} />

                {/* Search Bar (Only when open) */}
                {open && (
                    <Box sx={{ p: 1.5 }}>
                        <Autocomplete
                            freeSolo
                            fullWidth
                            size="small"
                            options={allMenuItems.map((option) => option.label)}
                            value={searchValue}
                            onInputChange={(event, newInputValue) => setSearchValue(newInputValue || "")}
                            onChange={handleAutocompleteChange}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Search Menu..." variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            backgroundColor: 'rgba(var(--drawer-color-rgb, 0, 0, 0), 0.05)',
                                            '& fieldset': { borderColor: 'rgba(var(--drawer-color-rgb, 0, 0, 0), 0.1)' },
                                            '&:hover fieldset': { borderColor: 'rgba(var(--drawer-color-rgb, 0, 0, 0), 0.3)' },
                                            '&.Mui-focused fieldset': { borderColor: 'var(--header-background)' },
                                        },
                                        '& .MuiInputBase-input': { color: 'var(--drawer-color)' },
                                        '& .MuiInputAdornment-root, & .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                                            color: 'var(--drawer-color)', opacity: 0.7
                                        }
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start"> <SearchIcon fontSize="small" /> </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <>
                                                {searchValue && (
                                                    <IconButton
                                                        aria-label="clear search"
                                                        onClick={() => setSearchValue("")}
                                                        edge="end"
                                                        size="small"
                                                        sx={{ color: 'var(--drawer-color)', opacity: 0.7 }}
                                                    >
                                                        <CloseIcon fontSize="inherit" />
                                                    </IconButton>
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                            clearOnBlur={false}
                        />
                    </Box>
                )}

                {/* Navigation List */}
                <List sx={{ ...(open ? {} : { marginTop: "50%" }), padding: 0, overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
                    {/* Static Home Item */}
                    <StyledListItemButton
                        key="home"
                        selected={isMenuActive('/home')}
                        onClick={() => handleNavigation('/home')}
                        sx={{ ...(open ? {} : { px: 2.5, justifyContent: 'center' }) }}
                        title={open ? "" : "Home"}
                    >
                        <StyledListItemIcon sx={{ ...(open ? {} : { mr: 'auto' }) }}> <HomeIcon /> </StyledListItemIcon>
                        <StyledListItemText primary="Home" sx={{ opacity: open ? 1 : 0 }} />
                    </StyledListItemButton>

                    {/* Dynamic Menu Items from props */}
                    {renderMenuItems(filteredDrawerItems)}
                </List>
            </DrawerStyled>

            {/* === Dialogs === */}

            {/* Theme Selection Dialog */}
            <Dialog open={isThemeDialogOpen} onClose={handleThemeDialogClose} PaperProps={{ sx: { bgcolor: 'var(--menu-background)', color: 'var(--menu-color)', borderRadius: '8px' } }}>
                <DialogTitle sx={{ borderBottom: '1px solid var(--divider-background)', pb: 1.5 }}>Select Theme</DialogTitle>
                <DialogContent sx={{ pt: '20px !important', minWidth: { xs: '280px', sm: '320px' } }}>
                    <List disablePadding>
                        {themes.map((themeOption) => (
                            <ListItem disablePadding key={themeOption.name}>
                                <ListItemButton
                                    onClick={() => handleThemeChange(themeOption.name)}
                                    selected={selectedTheme === themeOption.name}
                                    sx={{
                                        borderRadius: '6px', mb: 0.5,
                                        '&.Mui-selected': { backgroundColor: 'rgba(var(--menu-color-rgb, 0, 0, 0), 0.1)' },
                                        '&:hover': { backgroundColor: 'rgba(var(--menu-color-rgb, 0, 0, 0), 0.05)' }
                                    }}
                                >
                                    <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}>{themeOption.icon}</ListItemIcon>
                                    <ListItemText primary={themeOption.label} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid var(--divider-background)', p: '12px 24px' }}>
                    <Button onClick={handleThemeDialogClose} sx={{ color: 'inherit', opacity: 0.8 }}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Font Settings Dialog */}
            <Dialog open={isFontDialogOpen} onClose={handleFontDialogClose} PaperProps={{ sx: { bgcolor: 'var(--menu-background)', color: 'var(--menu-color)', borderRadius: '8px' } }}>
                <DialogTitle sx={{ borderBottom: '1px solid var(--divider-background)', pb: 1.5 }}>Font Settings</DialogTitle>
                <DialogContent sx={{ pt: '20px !important', minWidth: { xs: '280px', sm: '400px' } }}>
                    <FormControl fullWidth margin="normal" variant="outlined" sx={{
                        '& .MuiOutlinedInput-root': {
                            color: 'var(--menu-color)',
                            '& fieldset': { borderColor: 'rgba(var(--menu-color-rgb), 0.3)' },
                            '&:hover fieldset': { borderColor: 'rgba(var(--menu-color-rgb), 0.6)' },
                            '&.Mui-focused fieldset': { borderColor: 'var(--header-background)' },
                            '& .MuiSelect-icon': { color: 'var(--menu-color)' }
                        },
                        '& .MuiInputLabel-root': {
                            color: 'var(--menu-color)',
                            '&.Mui-focused': { color: 'var(--header-background)' }
                        }
                    }}>
                        <InputLabel id="font-family-select-label">Font Family</InputLabel>
                        <Select
                            labelId="font-family-select-label"
                            id="font-family-select"
                            value={selectedFontFamily}
                            onChange={handleFontFamilyChange}
                            label="Font Family"
                            MenuProps={{
                                PaperProps: { sx: { bgcolor: 'var(--menu-background)', color: 'var(--menu-color)', maxHeight: 300 } }
                            }}
                        >
                            {availableFontFamilies.map(font => (
                                <MenuItem key={font} value={font} sx={{ fontFamily: font === 'System Default' ? 'inherit' : `'${font}'`, fontSize: '0.95rem' }}>
                                    {font}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl component="fieldset" margin="normal" fullWidth>
                        <FormLabel component="legend" sx={{ color: 'var(--menu-color)', mb: 1, '&.Mui-focused': { color: 'var(--menu-color)' } }}>Font Size</FormLabel>
                        <RadioGroup
                            row
                            aria-label="font-size"
                            name="font-size-radio-group"
                            value={selectedFontSize}
                            onChange={handleFontSizeChange}
                            sx={{
                                justifyContent: 'flex-start',
                                flexWrap: 'wrap',
                                gap: 1,
                                color: 'var(--menu-color)'
                            }}
                        >
                            {fontSizes.map(size => (
                                <FormControlLabel
                                    key={size.value}
                                    value={size.value}
                                    control={<Radio sx={{ color: 'var(--menu-color)', '&.Mui-checked': { color: 'var(--header-background)' }, p: 0.5 }} size="small" />}
                                    label={`${size.label} (${size.value})`}
                                    sx={{ mr: 1 }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid var(--divider-background)', p: '12px 24px' }}>
                    <Button onClick={handleFontDialogClose} sx={{ color: 'inherit', opacity: 0.8 }}>Cancel</Button>
                    <Button onClick={applyFontSettings} variant="contained" sx={{ bgcolor: 'var(--header-background)', color: 'var(--header-color)', '&:hover': { bgcolor: 'var(--header-background)', opacity: 0.9 } }}>Apply</Button>
                </DialogActions>
            </Dialog>

            {/* === Profile Drawer === */}
            <SwipeableDrawer
                anchor="right"
                open={profileDrawerOpen}
                onClose={handleProfileDrawerToggle}
                onOpen={handleProfileDrawerToggle}
                PaperProps={{
                    sx: {
                        width: { xs: '80%', sm: drawerWidth + 80 },
                        bgcolor: 'var(--drawer-background)',
                        color: 'var(--drawer-color)',
                        boxShadow: theme.shadows[5],
                        borderLeft: 'none'
                    }
                }}
                ModalProps={{ keepMounted: true }}
                sx={{ zIndex: theme.zIndex.drawer + 2 }}
            >
                <Box sx={{ width: '100%' }} role="presentation">
                    <Toolbar sx={{ justifyContent: 'space-between', bgcolor: 'var(--header-background)', color: 'var(--header-color)' }}>
                        <Typography variant="h6">User Profile</Typography>
                        <IconButton onClick={handleProfileDrawerToggle} sx={{ color: 'inherit' }}> <CloseIcon /> </IconButton>
                    </Toolbar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, bgcolor: 'var(--header-background1)', color: 'var(--header-color)' }}>
                        <Avatar sx={{ width: 80, height: 80, mb: 2, fontSize: '2.5rem', bgcolor: 'var(--header-background)' }}>
                            {userDetails.initials}
                        </Avatar>
                        <Typography variant="h6" textAlign="center">{userDetails.fullName}</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }} textAlign="center">{userDetails.loginName}</Typography>
                    </Box>
                    <Card variant="outlined" sx={{
                        margin: 2,
                        backgroundColor: 'var(--menu-background)',
                        color: 'var(--menu-color)',
                        border: '1px solid var(--divider-background)'
                    }}>
                        <CardHeader title="Details" sx={{ pb: 0, '& .MuiTypography-root': { fontWeight: 'bold', color: 'var(--menu-color)' } }} />
                        <CardContent>
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 0.5, opacity: 0.8 }}>User ID:</Typography>
                                <Typography variant="body2" component="div">{userDetails.userID}</Typography>
                            </Box>
                            <Divider sx={{ my: 1, bgcolor: 'var(--divider-background)' }} />
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 0.5, opacity: 0.8 }}>Role:</Typography>
                                <Typography variant="body2" component="div">{userDetails.roleName}</Typography>
                            </Box>
                            <Divider sx={{ my: 1, bgcolor: 'var(--divider-background)' }} />
                            {userDetails.email && (
                                <>
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 0.5, opacity: 0.8 }}>Email:</Typography>
                                        <Typography variant="body2" component="div">{userDetails.email}</Typography>
                                    </Box>
                                    <Divider sx={{ my: 1, bgcolor: 'var(--divider-background)' }} />
                                </>
                            )}
                            {userDetails.mobile && (
                                <>
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 0.5, opacity: 0.8 }}>Mobile:</Typography>
                                        <Typography variant="body2" component="div">{userDetails.mobile}</Typography>
                                    </Box>
                                    <Divider sx={{ my: 1, bgcolor: 'var(--divider-background)' }} />
                                </>
                            )}
                            <Box>
                                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 0.5, opacity: 0.8 }}>Status:</Typography>
                                <Typography variant="body2" component="div">{userDetails.isActive ? 'Active ✅' : 'Inactive ❌'}</Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SwipeableDrawer>
        </Box>
    );
}



